import { useEffect, useRef, useCallback } from"react";
import { supabase } from"@/integrations/supabase/client";
import type { Json } from"@/integrations/supabase/types";

// Detect headless browsers / scrapers so they don't pollute analytics.
// Conservative: only obvious signals. Real users keep tracking; bots disappear.
const BOT_UA_RE =
 /bot|crawler|spider|spyder|headless|phantom|slurp|bytespider|petalbot|sogou|baidu|yandex|ahrefs|semrush|mj12|dataforseo|dotbot|blex|seznam|yisou|haosou|360spider|curl|wget|python-requests|go-http-client|java\/|okhttp/i;

function isLikelyBot(): boolean {
 if (typeof navigator ==="undefined") return true;
 try {
 if ((navigator as any).webdriver === true) return true;
 const ua = navigator.userAgent ||"";
 if (!ua || BOT_UA_RE.test(ua)) return true;
 if (!navigator.languages || navigator.languages.length === 0) return true;
 if (typeof screen !=="undefined" && (screen.width === 0 || screen.height === 0)) return true;
 } catch {
 return true;
 }
 return false;
}

let _botCache: boolean | null = null;
function shouldSkipTracking(): boolean {
 if (_botCache === null) _botCache = isLikelyBot();
 return _botCache;
}

// Generates or retrieves a persistent anonymous session ID
function getSessionId(): string {
 const key ="psa_session_id";
 let id = sessionStorage.getItem(key);
 if (!id) {
 id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
 sessionStorage.setItem(key, id);
 }
 return id;
}

interface AnalyticsEvent {
 session_id: string;
 user_id?: string | null;
 page: string;
 event_type: string;
 element_label?: string | null;
 value?: number | null;
 extra?: Json | null;
}

// Batch queue to avoid excessive DB writes
let queue: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush() {
 if (flushTimer) return;
 flushTimer = setTimeout(async () => {
 flushTimer = null;
 if (queue.length === 0) return;
 const batch = [...queue];
 queue = [];
 try {
 await supabase.from("page_analytics").insert(batch);
 } catch (e) {
 console.warn("Analytics flush failed:", e);
 }
 }, 2000);
}

function pushEvent(event: AnalyticsEvent) {
 if (shouldSkipTracking()) return;
 queue.push(event);
 scheduleFlush();
}

// Named sections on the landing page for hover tracking
const LANDING_SECTIONS = [
 { id:"hero", label:"Hero" },
 { id:"features", label:"Features" },
 { id:"how-it-works", label:"How It Works" },
 { id:"free-trial", label:"Free Trial" },
 { id:"competitors", label:"Competitors" },
 { id:"pricing", label:"Pricing" },
 { id:"cta", label:"CTA" },
 { id:"footer", label:"Footer" },
];

export function useAnalyticsTracker(page:"/" |"/dashboard", userId?: string | null) {
 const sessionId = useRef(getSessionId());
 const hoverTimers = useRef<Map<string, number>>(new Map());
 const scrollStopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
 const maxScrollDepth = useRef(0);
 const depthReported = useRef<Set<number>>(new Set());
 const sessionStart = useRef(Date.now());

 const track = useCallback(
 (event: Omit<AnalyticsEvent,"session_id" |"page">) => {
 pushEvent({
 session_id: sessionId.current,
 user_id: userId ?? null,
 page,
 ...event,
 });
 },
 [page, userId]
 );

 // ── Hover tracking ──────────────────────────────────────────────────────────
 useEffect(() => {
 const handleMouseOver = (e: MouseEvent) => {
 const target = e.target as HTMLElement;

 // Check named sections
 for (const section of LANDING_SECTIONS) {
 const el = document.getElementById(section.id);
 if (el && el.contains(target)) {
 const key = `section:${section.id}`;
 if (!hoverTimers.current.has(key)) {
 hoverTimers.current.set(key, Date.now());
 }
 return;
 }
 }

 // Check trackable elements
 const trackable = target.closest(
"[data-track], button, a[href], [role='button']"
 ) as HTMLElement | null;
 if (trackable) {
 const label =
 trackable.dataset.track ||
 trackable.getAttribute("aria-label") ||
 trackable.textContent?.trim().slice(0, 40) ||
"element";
 const key = `element:${label}`;
 if (!hoverTimers.current.has(key)) {
 hoverTimers.current.set(key, Date.now());
 }
 }
 };

 const handleMouseOut = (e: MouseEvent) => {
 const target = e.target as HTMLElement;

 // Sections
 for (const section of LANDING_SECTIONS) {
 const el = document.getElementById(section.id);
 if (el && el.contains(target)) {
 const key = `section:${section.id}`;
 const start = hoverTimers.current.get(key);
 if (start) {
 const duration = Date.now() - start;
 hoverTimers.current.delete(key);
 if (duration > 300) {
 track({ event_type:"hover", element_label: section.label, value: duration });
 }
 }
 return;
 }
 }

 // Elements
 const trackable = target.closest(
"[data-track], button, a[href], [role='button']"
 ) as HTMLElement | null;
 if (trackable) {
 const label =
 trackable.dataset.track ||
 trackable.getAttribute("aria-label") ||
 trackable.textContent?.trim().slice(0, 40) ||
"element";
 const key = `element:${label}`;
 const start = hoverTimers.current.get(key);
 if (start) {
 const duration = Date.now() - start;
 hoverTimers.current.delete(key);
 if (duration > 300) {
 track({
 event_type:"hover",
 element_label: label,
 value: duration,
 extra: { type:"element" } as Json,
 });
 }
 }
 }
 };

 document.addEventListener("mouseover", handleMouseOver, { passive: true });
 document.addEventListener("mouseout", handleMouseOut, { passive: true });

 return () => {
 document.removeEventListener("mouseover", handleMouseOver);
 document.removeEventListener("mouseout", handleMouseOut);
 };
 }, [track]);

 // ── Scroll stop & depth tracking ────────────────────────────────────────────
 useEffect(() => {
 const SCROLL_STOP_MS = 2000;
 const DEPTH_MILESTONES = [25, 50, 75, 90, 100];

 const handleScroll = () => {
 const docHeight = Math.max(document.body.scrollHeight - window.innerHeight, 1);
 const depth = Math.round((window.scrollY / docHeight) * 100);

 // Depth milestones
 for (const milestone of DEPTH_MILESTONES) {
 if (depth >= milestone && !depthReported.current.has(milestone)) {
 depthReported.current.add(milestone);
 maxScrollDepth.current = Math.max(maxScrollDepth.current, milestone);
 track({ event_type:"scroll_depth", value: milestone });
 }
 }

 // Scroll stop timer
 if (scrollStopTimer.current) clearTimeout(scrollStopTimer.current);
 scrollStopTimer.current = setTimeout(() => {
 const currentDepth = Math.round((window.scrollY / docHeight) * 100);
 track({
 event_type:"scroll_stop",
 value: currentDepth,
 extra: { scrollY: window.scrollY } as Json,
 });
 }, SCROLL_STOP_MS);
 };

 window.addEventListener("scroll", handleScroll, { passive: true });
 return () => {
 window.removeEventListener("scroll", handleScroll);
 if (scrollStopTimer.current) clearTimeout(scrollStopTimer.current);
 };
 }, [track]);

 // ── App close / tab hidden tracking ─────────────────────────────────────────
 useEffect(() => {
 const handleVisibilityChange = () => {
 if (document.visibilityState ==="hidden") {
 if (shouldSkipTracking()) return;
 const sessionDuration = Math.round((Date.now() - sessionStart.current) / 1000);
 const closeEvent: AnalyticsEvent = {
 session_id: sessionId.current,
 user_id: userId ?? null,
 page,
 event_type:"app_close",
 value: sessionDuration,
 extra: { maxScrollDepth: maxScrollDepth.current } as Json,
 };
 // Use sendBeacon with apikey header for authentication
 const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/page_analytics`;
 const headers = {
"Content-Type":"application/json",
"apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
"Prefer":"return=minimal",
 };
 // sendBeacon doesn't support custom headers, so use fetch with keepalive instead
 try {
 fetch(url, {
 method:"POST",
 headers,
 body: JSON.stringify([closeEvent]),
 keepalive: true,
 });
 } catch {
 // Last resort fallback
 supabase.from("page_analytics").insert([closeEvent]).then(() => {});
 }
 }
 };

 document.addEventListener("visibilitychange", handleVisibilityChange);
 return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
 }, [track, userId, page]);
}
